import type { AiOperationsCurrentProposal } from "@adpilot/shared";
import { useTranslation } from "react-i18next";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogClose,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogPanel,
  DialogPopup,
  DialogTitle,
} from "@/components/ui/dialog";

export function FullAnalysisDialog({
  onOpenChange,
  open,
  proposal,
}: {
  onOpenChange: (open: boolean) => void;
  open: boolean;
  proposal: AiOperationsCurrentProposal;
}): React.ReactElement {
  const { t } = useTranslation("aiOperations");
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogPopup className="max-w-[680px]">
        <DialogHeader>
          <DialogTitle>{t("runDetail.decisionRecord.analysis.title")}</DialogTitle>
          <DialogDescription>
            {t("runDetail.decisionRecord.analysis.description", { round: proposal.roundNumber })}
          </DialogDescription>
        </DialogHeader>
        <DialogPanel className="flex flex-col gap-5">
          <section>
            <Badge variant="neutral">
              {t(
                `runDetail.decisionRecord.summary.health.${proposal.artifact.analysis.campaignHealth}`,
              )}
            </Badge>
            <p className="mt-3 text-base font-semibold leading-6 text-ink">
              {proposal.artifact.analysis.summary}
            </p>
          </section>
          {proposal.artifact.analysis.keyFindings.length > 0 ? (
            <section>
              <h3 className="text-xs font-semibold uppercase tracking-[0.04em] text-muted-foreground">
                {t("runDetail.decisionRecord.analysis.findings")}
              </h3>
              <ul className="mt-3 flex flex-col gap-2">
                {proposal.artifact.analysis.keyFindings.map((finding) => (
                  <li key={finding} className="flex items-start gap-2 text-sm leading-5 text-ink">
                    <span className="mt-2 size-1.5 shrink-0 rounded-full bg-ink" />
                    {finding}
                  </li>
                ))}
              </ul>
            </section>
          ) : null}
          {proposal.artifact.conclusions.length > 0 ? (
            <section>
              <h3 className="text-xs font-semibold uppercase tracking-[0.04em] text-muted-foreground">
                {t("runDetail.decisionRecord.analysis.observations")}
              </h3>
              <div className="mt-3 flex flex-col gap-2">
                {proposal.artifact.conclusions.map((conclusion) => (
                  <article key={conclusion.id} className="rounded-[10px] border border-line p-3.5">
                    <div className="flex items-center justify-between gap-3">
                      <h4 className="text-[13px] font-semibold text-ink">
                        {conclusion.target.label}
                      </h4>
                      <Badge variant="outline">
                        {t(`runDetail.record.conclusion.${conclusion.agent.conclusion}`)}
                      </Badge>
                    </div>
                    <p className="mt-2 text-xs leading-5 text-muted-foreground">
                      {conclusion.agent.reasoning}
                    </p>
                  </article>
                ))}
              </div>
            </section>
          ) : null}
        </DialogPanel>
        <DialogFooter>
          <DialogClose render={<Button variant="outline" />}>
            {t("runDetail.decisionRecord.analysis.close")}
          </DialogClose>
        </DialogFooter>
      </DialogPopup>
    </Dialog>
  );
}
